<script lang="ts">
  import { onMount, tick } from "svelte";
  import Breadcrumb, { BreadcrumbItems } from "../../common/breadcrumb";
  import WaitlistFooter from "../../footer/waitlist/WaitlistFooter.svelte";
  import MainFooter from "../../footer/main-footer/MainFooter.svelte";
  import HightlightStyle from "./HightlightStyle.svelte";
  import marked from "marked";
  import hljs from "highlight.js/lib/core";
  import sql from "./sql_more";
  import javascript from "highlight.js/lib/languages/javascript";
  import { youtubeEmbedExtension } from "./MarkedYoutubeEmbed";
  import { codePenEmbedExtension } from "./CodePenEmbed";
  import { getDateString, waitBlogContentReady } from "../utils";
  import { blogList } from "../../../app/store/store-blog";
  import { initCodeSnippetEvent } from "./CodeSnippet";
  import pageUrl from "../../../PageUrl";
  import { setHeadTitle } from "../../../app/store/store-ui";

  export let blogId: string;
  export let isBlogExist: boolean;

  const blogItem = blogList.find((b) => b.id === blogId);
  let blogContent: string = "";
  let contentReady: boolean = false;
  let blogContentElement: HTMLElement;
  let removeCodeSnippetEvent: Function = () => {};
  let breadcrumbItems: BreadcrumbItems[] = blogItem && [
    {
      label: "Blog",
      href: pageUrl.BLOG,
      current: false,
    },
    // {
    //   label: "this tilte is for testing",
    //   href: `/blog/${blogId}`,
    //   current: false,
    // },
    {
      label: blogItem.title,
      href: `${pageUrl.BLOG}/${blogId}`,
      current: true,
    },
  ];

  const renderer = {
    blockquote(quote: String) {
      return `
        <div class="blockquote-container">
          <blockquote>
            ${quote}
          </blockquote>
        </div>
      `;
    },
  };

  // marked youtube extension
  marked.use({ renderer });
  marked.use({ extensions: [youtubeEmbedExtension, codePenEmbedExtension] });

  // register hightlight.js language
  hljs.registerLanguage("sql", sql);
  hljs.registerLanguage("javascript", javascript);

  function hightlightCode() {
    hljs.highlightAll();
    document
      .querySelectorAll(".blog-post-content :not(pre) code")
      .forEach((el: HTMLElement) => {
        hljs.highlightElement(el);
      });
  }

  async function getData() {
    if (!blogItem) {
      isBlogExist = false;
      return;
    }

    const dataFileURL = `/posts/${blogId}.txt`;
    const response: Response = await fetch(dataFileURL);

    if (response.ok) {
      const text = await response.text();
      blogContent = marked.parse(text);
    } else {
      isBlogExist = false;
    }

    await tick();
    hightlightCode();
    waitBlogContentReady(() => {
      contentReady = true;
    });
  }

  getData();

  $: if (blogContentElement && contentReady) {
    removeCodeSnippetEvent = initCodeSnippetEvent(blogContentElement);
  }

  onMount(() => {
    setHeadTitle(blogItem?.title || "");
    return () => {
      setHeadTitle("");
      removeCodeSnippetEvent()
    }
  });
</script>

<div class:hidden={!contentReady}>
  {#if blogItem}
    <div class="blog-post">
      <div class="blog-post-container">
        <div class="breadcrumb-container">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <p class="published-date">Published {getDateString(blogItem.time)}</p>

        <div class="blog-post-content" bind:this={blogContentElement}>
          <h1 class="blog-post-title">{blogItem.title}</h1>
          <h2 class="blog-post-subtitle">{blogItem.description}</h2>
          <p class="blog-post-cover-image">
            <img src={blogItem.image} alt={blogItem.title} />
          </p>
          {@html blogContent}
        </div>
      </div>
    </div>
  {/if}

  <WaitlistFooter />
  <MainFooter />
</div>

<HightlightStyle />

<style global lang="postcss">
  .hidden {
    display: none;
  }

  .blog-post {
    @apply mx-auto mb-24 pb-[60px] bg-white rounded;
    width: min(1088px, calc(100% - 20px));
    box-shadow: 0px 6px 24px -4px rgba(55, 84, 170, 0.12);
    z-index: 10;
  }

  .blog-post-container {
    @apply mx-auto;
    width: min(904px, calc(100% - 20px));
  }

  .blog-post-container .published-date {
    @apply mb-2 font-semibold text-pink-100 text-center;
    font-size: 15px;
    letter-spacing: 0.02em;
  }

  .breadcrumb-container {
    @apply pt-10 mb-7;
    font-size: 15px;
  }

  .breadcrumb-container nav {
    @apply mx-auto flex flex-row justify-center;
    width: min(904px, calc(100% - 20px));
  }

  /* title and cover image at the top of markdown, treat it specially*/
  .blog-post-content > h1.blog-post-title {
    @apply mx-auto mt-2 mb-3 typo-heading-2 text-center text-dark-300;
    max-width: fit-content;
    width: min(720px, calc(100% - 32px));
  }

  .blog-post-content > h2.blog-post-subtitle {
    @apply mx-auto mt-3 font-medium text-center text-dark-100;
    max-width: fit-content;
    font-size: 16px;
    width: min(720px, calc(100% - 32px));
  }

  /* cover image */
  .blog-post-content > p.blog-post-cover-image {
    @apply mt-5 mb-8 relative overflow-hidden;
    width: 100%;
    padding-top: 54.21%;
  }

  .blog-post-content > p.blog-post-cover-image img:first-child {
    @apply absolute top-0 bottom-0 left-0 right-0 my-0 rounded-none;
    min-width: 100%;
    min-height: 100%;
    object-fit: cover;
  }

  .blog-post-content p img {
    @apply my-8 rounded-lg;
  }

  /* blog post content */
  .blog-post-content {
    @apply text-dark-100;
    line-height: 1.7;
    font-size: 15px;
  }

  .blog-post-content h1 {
    @apply my-8;
    font-size: 30px;
    line-height: 1.5;
  }

  .blog-post-content h2 {
    @apply mt-6 mb-4;
    font-size: 24px;
    line-height: 1.42;
  }

  .blog-post-content h3 {
    @apply my-4 text-left text-dark-200 font-medium;
    font-size: 18px;
    line-height: 1.5;
  }

  .blog-post-content h1,
  .blog-post-content h2 {
    @apply text-left text-dark-300 font-semibold;
    /* line-height: 1.2; */
    letter-spacing: 0.02;
  }

  .blog-post-content p {
    @apply my-6;
  }

  /* link style */
  .blog-post-content a {
    @apply text-primary-indigo-blue;
  }

  .blog-post-content a:hover {
    @apply underline;
  }

  /* block quote */
  .blog-post-content .blockquote-container {
    @apply relative z-0;
  }

  .blog-post-content blockquote {
    @apply relative mt-4 mb-6 mr-auto bg-white rounded-md;
    padding: 20px 20px 20px 32px;
    border: 1px solid #f7f9fa;
    background-image: url(/images/blog/blockquote.svg);
    background-repeat: no-repeat;
    background-position: right 8px bottom 4px;
    max-width: 720px;
  }

  .blog-post-content blockquote::before {
    @apply absolute rounded-lg bg-[#3754aa] bg-opacity-10;
    content: "";
    left: 12px;
    right: 12px;
    top: 12px;
    bottom: -8px;
    z-index: -1;
    filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }

  .blog-post-content blockquote::after {
    @apply absolute bg-primary-indigo-blue;
    content: "";
    left: 2px;
    top: 2px;
    bottom: 2px;
    width: 4px;
    border-radius: 6px 0px 0px 6px;
  }

  .blog-post-content blockquote p {
    @apply m-0 text-dark-100 font-medium;
    line-height: 1.7;
    font-size: 15px;
  }

  .blog-post-content blockquote p cite {
    @apply relative block pt-4 uppercase not-italic font-semibold text-primary-indigo-blue;
    font-size: 15px;
    letter-spacing: 0.06em;
  }

  .blog-post-content blockquote cite::before {
    content: "- ";
  }

  /* ordered and unordered list */
  .blog-post-content ul,
  .blog-post-content ol {
    @apply my-4 space-y-3.5;
    list-style: none;
  }

  .blog-post-content ul li {
    @apply relative pl-5;
  }

  .blog-post-content ol li {
    @apply relative pl-6;
  }

  .blog-post-content ul li::before {
    @apply absolute rounded-full bg-dark-200;
    content: "";
    width: 8px;
    height: 8px;
    top: 9px;
    left: 0px;
  }

  .blog-post-content ol {
    counter-reset: ordered-list-counter;
  }

  .blog-post-content ol li {
    counter-increment: ordered-list-counter;
  }

  .blog-post-content ol li::before {
    @apply absolute rounded-full text-dark-200 font-semibold;
    content: counter(ordered-list-counter) ".";
    top: 0px;
    left: 0px;
    font-size: 15px;
  }

  /* code snippet */
  .blog-post-content pre {
    @apply relative my-8 px-5 py-4 rounded-lg bg-light-50;
  }

  .blog-post-content pre button {
    @apply absolute flex flex-row items-center justify-center right-2 top-2 w-7 h-7 border-0 rounded-sm;
  }

  .blog-post-content pre button:hover,
  .blog-post-content pre button:active {
    @apply text-dark-300;
  }

  /* inline code */
  .blog-post-content code {
    @apply font-medium;
    font-family: "Consolas BoldItalic", Consolas, "Courier New", monospace;
  }

  .blog-post-container pre {
    @apply rounded-lg;
  }

  .blog-post-content pre code {
    @apply block overflow-x-auto;
    max-width: 100%;
  }

  .blog-post-content :not(pre) code {
    @apply px-3 py-1 bg-light-50 rounded;
  }

  .blog-post-content pre,
  .blog-post-content :not(pre) code {
    @apply border-solid border border-light-100;
    box-shadow: 0px 5px 4px -3px rgba(69, 68, 80, 0.12);
  }

  .blog-post-content strong {
    @apply text-dark-300;
  }

  /* horizontal separator or devider */
  .blog-post-content hr,
  .blog-post-content hr::before,
  .blog-post-content hr::after {
    @apply rounded-full;
    width: 6px;
    height: 6px;
    background-color: #d9d9d9;
  }

  .blog-post-content hr {
    @apply block relative mx-auto my-6 rounded-full border-0 overflow-visible;
  }

  .blog-post-content hr::before,
  .blog-post-content hr::after {
    @apply absolute top-0;
    content: "";
  }

  .blog-post-content hr::before {
    left: -12px;
  }

  .blog-post-content hr::after {
    left: calc(100% + 6px);
  }

  /* table style */
  .blog-post-content table,
  .blog-post-content td,
  .blog-post-content th {
    @apply border border-solid border-dark-50;
  }

  .blog-post-content table {
    width: 100%;
  }

  .blog-post-content th {
    @apply text-dark-200;
  }

  .blog-post-content td,
  .blog-post-content th {
    @apply p-2 text-left;
  }
</style>
